import type { TFunction } from 'i18next';

import { WebSocketDataLabelCodeEnum } from '../../enums/WebSocketDataLabelCodeEnum';
import { WebSocketEventTypeEnum } from '../../enums/WebSocketEventTypeEnum';
import type Notification from '../../interfaces/Notification';
import { resolveNotificationFileRef } from './resolveAiEventFileRef';

const SHARING_LABEL_CODES = new Set<string>(Object.values(WebSocketDataLabelCodeEnum));

const normalizeKey = (value: string): string => value.trim().toLowerCase();

function isAiClassificationErrorStatus(status?: string): boolean {
  return status?.trim().toLowerCase() === 'error';
}

export type NotificationCopySource = Pick<
  Notification,
  | 'eventType'
  | 'file_id'
  | 'labelCode'
  | 'params'
  | 'status'
  | 'errorMessage'
  | 'file'
  | 'batch_id'
  | 'batchProcessed'
  | 'batchTotal'
> & {
  id?: string;
  description?: string;
  sourcePayload?: Record<string, unknown>;
};

type BatchCounts = { processed: number; total: number };

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function readSourcePayloadData(
  sourcePayload?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const data = sourcePayload?.data;
  return data != null && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
}

function parseBatchCountsFromDescription(description?: string): BatchCounts | null {
  const text = description?.trim();
  if (!text) {
    return null;
  }

  const slashMatch = text.match(/\((\d+)\/(\d+)\)/);
  if (slashMatch) {
    return { processed: Number(slashMatch[1]), total: Number(slashMatch[2]) };
  }

  const ofMatch = text.match(/(\d+)\s+(?:of|di|de)\s+(\d+)/i);
  if (ofMatch) {
    return { processed: Number(ofMatch[1]), total: Number(ofMatch[2]) };
  }

  return null;
}

function resolveBatchCounts(notification: NotificationCopySource): BatchCounts | null {
  const processedFromRoot = readNumber(notification.batchProcessed);
  const totalFromRoot = readNumber(notification.batchTotal);
  if (processedFromRoot != null && totalFromRoot != null) {
    return { processed: processedFromRoot, total: totalFromRoot };
  }

  const payloadData = readSourcePayloadData(notification.sourcePayload);
  const processedFromPayload = readNumber(payloadData?.processed);
  const totalFromPayload = readNumber(payloadData?.total);
  if (processedFromPayload != null && totalFromPayload != null) {
    return { processed: processedFromPayload, total: totalFromPayload };
  }

  return parseBatchCountsFromDescription(notification.description);
}

function isBatchTagsNotification(notification: NotificationCopySource): boolean {
  const counts = resolveBatchCounts(notification);
  if (counts != null) {
    return counts.total > 1;
  }

  const totalFromRoot = readNumber(notification.batchTotal);
  if (totalFromRoot != null) {
    return totalFromRoot > 1;
  }

  const payloadData = readSourcePayloadData(notification.sourcePayload);
  const totalFromPayload = readNumber(payloadData?.total);
  return totalFromPayload != null && totalFromPayload > 1;
}

/** Live title from notification fields — always current locale (not persisted bake). */
export function resolveNotificationTitle(
  t: TFunction,
  notification: NotificationCopySource,
): string {
  switch (normalizeKey(String(notification.eventType ?? ''))) {
    case WebSocketEventTypeEnum.Tags:
      if (isBatchTagsNotification(notification)) {
        if (isAiClassificationErrorStatus(notification.status)) {
          return t('notifications.tags.batchFailedTitle');
        }
        return t('notifications.tags.batchSuccessTitle');
      }
      if (isAiClassificationErrorStatus(notification.status)) {
        return t('notifications.tags.errorTitle');
      }
      return t('notifications.tags.title');
    case WebSocketEventTypeEnum.Summary:
      return t('notifications.summary.title');
    case WebSocketEventTypeEnum.DocumentClassification:
      return t('notifications.documentClassification.title');
    case WebSocketEventTypeEnum.SharingOperation:
      return t('notifications.sharing.title');
    default:
      return t('notifications.default.title');
  }
}

/** Live description from notification fields — always current locale. */
export function resolveNotificationDescription(
  t: TFunction,
  notification: NotificationCopySource,
): string {
  if (notification.labelCode != null && String(notification.labelCode).trim() !== '') {
    const normalizedLabelCode = normalizeKey(String(notification.labelCode));
    const labelCode = SHARING_LABEL_CODES.has(normalizedLabelCode)
      ? normalizedLabelCode
      : WebSocketDataLabelCodeEnum.SharingOperationUnknown;
    return t(`notifications.sharing.${labelCode}`, notification.params ?? {});
  }

  switch (normalizeKey(String(notification.eventType ?? ''))) {
    case WebSocketEventTypeEnum.Tags: {
      if (isBatchTagsNotification(notification)) {
        const counts = resolveBatchCounts(notification);
        if (counts) {
          const { processed, total } = counts;
          if (isAiClassificationErrorStatus(notification.status)) {
            return t('notifications.tags.batchFailedDescription', { processed, total });
          }
          return t('notifications.tags.batchSuccessDescription', { processed, total });
        }
        if (isAiClassificationErrorStatus(notification.status)) {
          return t('notifications.tags.batchFailedDescription', { processed: 0, total: 0 });
        }
        return t('notifications.tags.batchSuccessDescription', { processed: 0, total: 0 });
      }
      if (isAiClassificationErrorStatus(notification.status)) {
        return notification.errorMessage || t('notifications.tags.error');
      }
      const fileName = notification.file?.fileName?.trim();
      if (fileName) {
        return t('notifications.tags.descriptionWithFileName', { fileName });
      }
      const fileRef = resolveNotificationFileRef(notification);
      if (fileRef != null) {
        return t('notifications.tags.description', { fileId: fileRef });
      }
      return t('notifications.tags.descriptionGeneric');
    }
    case WebSocketEventTypeEnum.Summary: {
      const fileRef = resolveNotificationFileRef(notification);
      return t('notifications.summary.description', { fileId: fileRef });
    }
    case WebSocketEventTypeEnum.DocumentClassification: {
      const fileRef = resolveNotificationFileRef(notification);
      return t('notifications.documentClassification.description', {
        fileId: fileRef,
      });
    }
    default:
      return t('notifications.default.description');
  }
}
